import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
  styled,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import AccountPopover from "../AccountPopover/AccountPopover";
import { CompanyAvatar } from "../CompanyAvatar/CompanyAvatar";
import { hasTbsRole } from "../../utils/tbsTenant";
import { useTbsTenant } from "../../hooks/tbs/useTbsTenant";
import { visibleNavigation, type TbsNavSection } from "../../pages/tbs/shared/navigation";
import { NAV_TEXTS } from "../../pages/tbs/shared/constants";
import TenantSelector from "./TenantSelector";
import NavIcon from "./navIcons";

// The header every management screen wears — `NAV-1` (`TBS-77`).
//
// **Not `HeaderBar`, deliberately, and not a copy of it either.** P0-6 reused the storefront's and
// it was wrong twice over: it puts a cart, a catalog search and the shop's own menu on top of a POS,
// and it reads `state.cart`, dragging storefront Redux state into screens that have nothing to do
// with the shop. What is borrowed here is the *look* — the same logo, the same white sticky bar,
// the same account button — because it is the same company and the same people.
//
// `AccountPopover` **is** reused, and that is not a contradiction: it is an avatar with a log-out
// button in it and carries no storefront state at all. Rebuilding it would give the two apps two
// ways to sign out of one Auth0 session.
//
// **The navigation is filtered by role** (`hasTbsRole`, the same claim the API's authorizer reads).
// A cashier is not shown Configuración or Traslados: those routes answer with the guard's refusal
// and the API with a 403, and a menu whose entries fail is worse than a shorter menu.

/** Below this the bar becomes a hamburger: the POS is used standing up, sometimes on a tablet. */
const DESKTOP = "md";

const Bar = styled(Box)(({ theme }) => ({
  position: "sticky",
  top: 0,
  zIndex: theme.zIndex.appBar,
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  height: 64,
  paddingLeft: theme.spacing(1.5),
  paddingRight: theme.spacing(1.5),
  backgroundColor: theme.palette.common.white,
  boxShadow: theme.shadows[4],
}));

const Brand = styled(RouterLink)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  textDecoration: "none",
  color: theme.palette.text.primary,
  flexShrink: 0,
}));

const BrandName = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  whiteSpace: "nowrap",
  [theme.breakpoints.down("sm")]: { display: "none" },
}));

const NavRow = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(0.5),
  flex: 1,
  overflowX: "auto",
}));

/** Pushes the account controls to the right on mobile, where there is no nav row to do it. */
const Spacer = styled(Box)({ flex: 1 });

const navButtonSx = {
  color: "text.primary",
  textTransform: "none" as const,
  fontWeight: 600,
  whiteSpace: "nowrap" as const,
};

const activeSx = { ...navButtonSx, color: "primary.main" };

/** A section is "where you are" when the current path is one of its screens, or below one. */
const isCurrent = (section: TbsNavSection, pathname: string): boolean =>
  section.items.some((item) => pathname === item.path || pathname.startsWith(`${item.path}/`));

export const TbsHeader: React.FC = () => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up(DESKTOP));
  const { pathname } = useLocation();
  const { user } = useAuth0();
  const { tenantId } = useTbsTenant();

  const sections = useMemo(
    () => visibleNavigation((role) => hasTbsRole(user?.tbs_tenants, tenantId, role)),
    [user, tenantId],
  );

  // One open menu at a time, keyed by section label — several `anchorEl` states would let two
  // menus be open at once on a fast click.
  const [menu, setMenu] = useState<{ label: string; anchor: HTMLElement } | null>(null);
  const [drawer, setDrawer] = useState(false);

  const brand = (
    <Brand to="/gestion" aria-label={NAV_TEXTS.home}>
      <CompanyAvatar />
      <BrandName variant="subtitle1">{NAV_TEXTS.brand}</BrandName>
    </Brand>
  );

  // Rendered once and placed by `isDesktop` rather than mounted twice and hidden with CSS: two
  // copies would put every link in the DOM twice, which screen readers announce twice and
  // `getByRole` finds twice.
  const account = (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
      <TenantSelector dense />
      <AccountPopover />
    </Box>
  );

  const desktopNav = (
    <NavRow component="nav" aria-label={NAV_TEXTS.navLabel}>
      {sections.map((section) =>
        section.items.length === 1 ? (
          <Button
            key={section.label}
            component={RouterLink}
            to={section.items[0].path}
            sx={isCurrent(section, pathname) ? activeSx : navButtonSx}
            startIcon={<NavIcon icon={section.icon} fontSize="small" />}
          >
            {section.label}
          </Button>
        ) : (
          <Button
            key={section.label}
            aria-haspopup="menu"
            aria-expanded={menu?.label === section.label ? true : undefined}
            onClick={(event) => setMenu({ label: section.label, anchor: event.currentTarget })}
            sx={isCurrent(section, pathname) ? activeSx : navButtonSx}
            startIcon={<NavIcon icon={section.icon} fontSize="small" />}
            endIcon={<ExpandMoreIcon />}
          >
            {section.label}
          </Button>
        ),
      )}
    </NavRow>
  );

  const openSection = sections.find((section) => section.label === menu?.label);

  const drawerNav = (
    <Drawer anchor="left" open={drawer} onClose={() => setDrawer(false)}>
      <Box sx={{ width: 280 }} role="presentation" onClick={() => setDrawer(false)}>
        {sections.map((section, index) => (
          <React.Fragment key={section.label}>
            {index > 0 && <Divider />}
            <List
              dense
              subheader={
                <ListSubheader disableSticky sx={{ bgcolor: "transparent" }}>
                  {section.label}
                </ListSubheader>
              }
            >
              {section.items.map((item) => (
                <ListItemButton key={item.path} component={RouterLink} to={item.path}>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <NavIcon icon={section.icon} fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary={item.label} />
                </ListItemButton>
              ))}
            </List>
          </React.Fragment>
        ))}
      </Box>
    </Drawer>
  );

  return (
    <Bar component="header">
      {!isDesktop && (
        <IconButton
          aria-label={NAV_TEXTS.openMenu}
          aria-haspopup="menu"
          onClick={() => setDrawer(true)}
          edge="start"
        >
          <MenuIcon />
        </IconButton>
      )}

      {brand}

      {isDesktop ? desktopNav : <Spacer />}

      {account}

      {isDesktop ? (
        <Menu
          open={Boolean(menu)}
          anchorEl={menu?.anchor ?? null}
          onClose={() => setMenu(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
        >
          {(openSection?.items ?? []).map((item) => (
            <MenuItem
              key={item.path}
              component={RouterLink}
              to={item.path}
              onClick={() => setMenu(null)}
            >
              {item.label}
            </MenuItem>
          ))}
        </Menu>
      ) : (
        drawerNav
      )}
    </Bar>
  );
};

export default TbsHeader;
